import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { useTranslation } from '@govard/i18n-core/react';
import { Alert, Card, Select, Statistic, Table, Tag, Typography } from 'antd';
import { useState, type ReactElement } from 'react';

import type { OperationsWindow, PublicDeliveryOutcome } from '../api/telegram-operations.client.js';
import { useTelegramOperations } from '../hooks/useTelegramOperations.js';
import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { LIST_SURFACE_STYLE } from '../../../shared/ui/list-styles.js';
import { QuietEmpty, QuietListSkeleton } from '../../../shared/ui/quiet-states.js';

const { Text } = Typography;

/** Spec D `.kpi`-ячейка внутри hairline-полосы статистики. */
const statisticCellStyles = {
  padding: '12px 16px',
  background: 'var(--ant-color-bg-container)',
  border: 'none',
} as const;
const PUBLIC_OUTCOMES: ReadonlySet<string> = new Set([
  'subscription_disabled',
  'authority_unavailable',
  'provider_retryable',
  'provider_recipient_unavailable',
  'privacy_revoked',
  'other',
] satisfies PublicDeliveryOutcome[]);

export function TelegramOperationsPage(): ReactElement {
  const { t } = useTranslation();
  const [window, setWindow] = useState<OperationsWindow>('24h');
  const scope = useCurrentScope();
  const query = useTelegramOperations(window);
  const data = query.data;
  if (scope !== null && !['owner', 'admin'].includes(scope.role))
    return <Alert type="warning" showIcon title={t('crm.telegram_operations.forbidden')} />;
  const duration = (value: number | null) =>
    value === null
      ? '—'
      : new Intl.NumberFormat(undefined, {
          style: 'unit',
          unit: 'millisecond',
          unitDisplay: 'short',
          maximumFractionDigits: 0,
        }).format(value);
  return (
    <div className="flex flex-col gap-3" data-testid="telegram-operations-page">
      <PageHeader
        title={t('crm.telegram_operations.title')}
        subtitle={t('crm.telegram_operations.subtitle')}
        actions={
          <Select
            aria-label={t('crm.telegram_operations.window')}
            value={window}
            onChange={setWindow}
            style={{ width: 140, fontSize: 12 }}
            options={(['1h', '24h', '7d'] as const).map((value) => ({
              value,
              label: t(`crm.telegram_operations.windows.${value}`),
            }))}
          />
        }
      />
      {query.isLoading ? (
        <div style={LIST_SURFACE_STYLE}>
          <QuietListSkeleton rows={6} />
        </div>
      ) : query.isError ? (
        <ErrorState
          title={t('crm.telegram_operations.load_error')}
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : data ? (
        <>
          {data.alerts.map((alert) => (
            <Alert
              key={alert.code}
              type={alert.severity === 'critical' ? 'error' : 'warning'}
              showIcon
              title={t(`crm.telegram_operations.alerts.${alert.code}`)}
            />
          ))}
          {/* Spec D `.kpis`: единая hairline-полоса статистики, не восемь карточек. */}
          <div
            className="grid grid-cols-2 overflow-hidden sm:grid-cols-4"
            style={{
              background: 'var(--ant-color-border-secondary)',
              border: '1px solid var(--ant-color-border-secondary)',
              borderRadius: 'var(--ant-border-radius-lg, 14px)',
            }}
          >
            <Statistic
              title={t('crm.telegram_operations.ingress')}
              value={data.ingress.total}
              styles={{
                root: statisticCellStyles,
                content: { fontVariantNumeric: 'tabular-nums' },
              }}
            />
            <Statistic
              title={t('crm.telegram_operations.delivery')}
              value={data.delivery.total}
              styles={{
                root: statisticCellStyles,
                content: { fontVariantNumeric: 'tabular-nums' },
              }}
            />
            <Statistic
              title={t('crm.telegram_operations.inbound_due')}
              value={data.ingress.dueDepth}
              styles={{
                root: statisticCellStyles,
                content: { fontVariantNumeric: 'tabular-nums' },
              }}
            />
            <Statistic
              title={t('crm.telegram_operations.delivery_due')}
              value={data.delivery.dueDepth}
              styles={{
                root: statisticCellStyles,
                content: { fontVariantNumeric: 'tabular-nums' },
              }}
            />
            <Statistic
              title={t('crm.telegram_operations.delivery_success')}
              value={data.delivery.successRate === null ? '—' : data.delivery.successRate * 100}
              suffix={data.delivery.successRate === null ? undefined : '%'}
              precision={1}
              styles={{
                root: statisticCellStyles,
                content: { fontVariantNumeric: 'tabular-nums' },
              }}
            />
            <Statistic
              title={t('crm.telegram_operations.ambiguous')}
              value={data.delivery.ambiguousCount}
              styles={{
                root: statisticCellStyles,
                content: { fontVariantNumeric: 'tabular-nums' },
              }}
            />
            <Statistic
              title={t('crm.telegram_operations.ingress_p95')}
              value={duration(data.ingress.p95ProcessingMs)}
              styles={{ root: statisticCellStyles }}
            />
            <Statistic
              title={t('crm.telegram_operations.delivery_p95')}
              value={duration(data.delivery.p95LatencyMs)}
              styles={{ root: statisticCellStyles }}
            />
          </div>
          <Card title={t('crm.telegram_operations.rollout')}>
            <div className="flex flex-wrap gap-2">
              {Object.entries(data.flags).map(([key, enabled]) => (
                <EntityStatusChip key={key} tone={enabled ? 'success' : 'default'}>
                  {t(`crm.telegram_operations.flags.${key}`)}:{' '}
                  {enabled ? t('crm.telegram_operations.on') : t('crm.telegram_operations.off')}
                </EntityStatusChip>
              ))}
            </div>
          </Card>
          <Card title={t('crm.telegram_operations.connections')}>
            {data.connections.length === 0 ? (
              <QuietEmpty text={t('crm.telegram_operations.no_connections')} />
            ) : (
              <div className="flex flex-col">
                {data.connections.map((connection, index) => (
                  <div
                    key={connection.ref ?? index}
                    className="flex flex-wrap items-center justify-between gap-2"
                    style={{
                      padding: '9px 14px',
                      borderTop:
                        index === 0 ? 'none' : '1px solid var(--ant-color-border-secondary)',
                    }}
                  >
                    <Text code>
                      {connection.ref ?? t('crm.telegram_operations.ref_unavailable')}
                    </Text>
                    <Tag>{t(`crm.telegram_operations.connection_status.${connection.status}`)}</Tag>
                  </div>
                ))}
              </div>
            )}
          </Card>
          <Card title={t('crm.telegram_operations.recent')} styles={{ body: { padding: 0 } }}>
            {data.recent.length === 0 ? (
              <QuietEmpty text={t('crm.telegram_operations.empty')} />
            ) : (
              <Table
                rowKey="ref"
                pagination={false}
                scroll={{ x: 720 }}
                dataSource={data.recent}
                columns={[
                  { title: t('crm.telegram_operations.ref'), dataIndex: 'ref' },
                  {
                    title: t('crm.telegram_operations.kind'),
                    dataIndex: 'kind',
                    render: (value: string) => t(`crm.telegram_operations.kinds.${value}`),
                  },
                  {
                    title: t('crm.telegram_operations.status'),
                    dataIndex: 'status',
                    render: (value: string) => (
                      <Tag>{t(`crm.telegram_operations.statuses.${value}`)}</Tag>
                    ),
                  },
                  { title: t('crm.telegram_operations.attempt'), dataIndex: 'attempt' },
                  {
                    title: t('crm.telegram_operations.duration'),
                    dataIndex: 'durationMs',
                    render: duration,
                  },
                  {
                    title: t('crm.telegram_operations.outcome'),
                    dataIndex: 'outcomeCode',
                    render: (value: PublicDeliveryOutcome | null) =>
                      value === null
                        ? '—'
                        : t(
                            `crm.telegram_operations.outcomes.${PUBLIC_OUTCOMES.has(value) ? value : 'other'}`,
                          ),
                  },
                ]}
              />
            )}
          </Card>
        </>
      ) : null}
    </div>
  );
}
